/**
 * CompanyContext provides company state, membership, and permissions to React components.
 * Follows the useAuth pattern from hooks/useAuth.tsx.
 *
 * CONTEXT.md Decisions:
 * - TeamRole governs company pages (not UserRole)
 * - Users can belong to multiple companies (future support)
 * - Standard role hierarchy: OWNER > ADMIN > AGENT > VIEWER
 * - No company = hide company sections
 * - 403 (not member) is not an error state
 */

import { createContext, useEffect, useMemo, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { fetchMyTeam } from '../services/companyService';
import { readPersistedValue, writePersistedValue } from '../utils/sessionPersistence';
import type { Company, TeamRole, CompanyMembership, TeamMember } from '../types/company';
import { ApiError } from '../utils/apiClient';

const COMPANY_STORAGE_KEY = 'safaqat24.company.selected';

export interface CompanyContextType {
  /** Current active company */
  company: Company | null;
  /** User's role in current company */
  myRole: TeamRole | null;
  /** All companies user belongs to */
  companies: CompanyMembership[];
  /** Team members of current company */
  teamMembers: TeamMember[];
  /** Switch to a different company (for multi-company support) */
  switchCompany: (companyId: string) => Promise<void>;
  /** Check if user has permission based on role hierarchy */
  hasPermission: (requiredRole: TeamRole) => boolean;
  /** Loading state while fetching company data */
  isLoading: boolean;
  /** Error message if company fetch failed */
  error: string | null;
  /** Refetch company data */
  refetch: () => Promise<void>;
}

export const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

/**
 * Role hierarchy per CONTEXT.md:
 * - OWNER: Everything + transfer ownership + manage subscription
 * - ADMIN: Manage team + settings
 * - AGENT: Manage own listings
 * - VIEWER: Read-only
 */
const ROLE_HIERARCHY: Record<TeamRole, number> = {
  OWNER: 4,
  ADMIN: 3,
  AGENT: 2,
  VIEWER: 1,
};

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [myRole, setMyRole] = useState<TeamRole | null>(null);
  const [companies, setCompanies] = useState<CompanyMembership[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch company data
  // Note: selectedCompanyId is reserved for future multi-company support
  const fetchCompanyData = useCallback(async (_selectedCompanyId?: string) => {
    if (!session?.token) {
      setIsLoading(false);
      setCompany(null);
      setMyRole(null);
      setCompanies([]);
      setTeamMembers([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const teamResponse = await fetchMyTeam(session.token);

      // User may not be in any company - that's OK
      if (!teamResponse?.companyId) {
        setCompany(null);
        setMyRole(null);
        setCompanies([]);
        setTeamMembers([]);
        setIsLoading(false);
        return;
      }

      // Build company from team response
      const currentCompany: Company = {
        id: teamResponse.companyId,
        name: teamResponse.companyName,
        maxListings: teamResponse.usage.maxAllowed,
        usagePercent: teamResponse.usage.maxAllowed > 0
          ? (teamResponse.usage.currentCount / teamResponse.usage.maxAllowed) * 100
          : 0,
      };

      // Find current user's role
      const currentUserMember = teamResponse.members.find(m => m.isCurrentUser);
      const userRole = currentUserMember?.role || null;

      // For now, user can only be in one company (via Agent record)
      // Future: support multiple companies via CompanyTeamMember
      const membershipList: CompanyMembership[] = [{
        companyId: teamResponse.companyId,
        companyName: teamResponse.companyName,
        role: userRole || 'VIEWER',
      }];

      setCompany(currentCompany);
      setMyRole(userRole);
      setCompanies(membershipList);
      setTeamMembers(teamResponse.members);

      // Persist selected company
      await writePersistedValue(COMPANY_STORAGE_KEY, currentCompany.id);
    } catch (err) {
      // 403 = not a company member, 404 = no company exists — neither is an error state
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        setCompany(null);
        setMyRole(null);
        setCompanies([]);
        setTeamMembers([]);
      } else {
        console.error('Failed to fetch company data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load company data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [session?.token]);

  // Initial fetch and restore
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      const storedCompanyId = await readPersistedValue<string>(COMPANY_STORAGE_KEY);
      if (isMounted && session?.token) {
        await fetchCompanyData(storedCompanyId || undefined);
      } else if (isMounted) {
        setIsLoading(false);
      }
    };

    init();

    return () => { isMounted = false; };
  }, [session?.token, fetchCompanyData]);

  // Switch company (for future multi-company support)
  const switchCompany = useCallback(async (companyId: string) => {
    await writePersistedValue(COMPANY_STORAGE_KEY, companyId);
    await fetchCompanyData(companyId);
  }, [fetchCompanyData]);

  // Check if user has permission based on role hierarchy
  const hasPermission = useCallback((requiredRole: TeamRole): boolean => {
    if (!myRole) return false;
    return ROLE_HIERARCHY[myRole] >= ROLE_HIERARCHY[requiredRole];
  }, [myRole]);

  const value = useMemo<CompanyContextType>(() => ({
    company,
    myRole,
    companies,
    teamMembers,
    switchCompany,
    hasPermission,
    isLoading,
    error,
    refetch: fetchCompanyData,
  }), [company, myRole, companies, teamMembers, switchCompany, hasPermission, isLoading, error, fetchCompanyData]);

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}
